import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  GridColumnMenu,
  GridColumnMenuProps,
} from '@mui/x-data-grid-pro';
import {
  Autocomplete,
  Box,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Popover,
  TextField,
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import {
  useGetApiContactsQuery,
  useGetApiProgramsAvailableDeliverableTypesQuery,
  useGetApiProjectsQuery,
} from '../../../../app/athenaApiGen';
import {
  setAuthorIdFilter,
  setProjectIdFilter,
  setTypeIdFilter,
} from '../../../../app/deliverables/deliverablesSlice';
import { RootState } from '../../../../app/store';

// Generic option shape every filter menu item maps to.
type FilterOption = {
  id: string;
  label: string;
};

// Generic "open Popover with Autocomplete" filter menu item. Each column-
// specific wrapper supplies its data + selected id + dispatch handlers.
function FilterMenuItem(props: {
  label: string;
  clearLabel: string;
  options: FilterOption[];
  selectedId: string | null;
  onSelect: (id: string | null) => void;
  searchPlaceholder: string;
}) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const selected =
    props.options.find((o) => o.id === props.selectedId) ?? null;
  return (
    <>
      <MenuItem onClick={(e) => setAnchorEl(e.currentTarget)}>
        <ListItemIcon>
          <FilterListIcon fontSize="small" />
        </ListItemIcon>
        <ListItemText>{props.label}</ListItemText>
      </MenuItem>
      {props.selectedId && (
        <MenuItem onClick={() => props.onSelect(null)}>
          <ListItemIcon>
            <FilterListOffIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{props.clearLabel}</ListItemText>
        </MenuItem>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
        transformOrigin={{ vertical: 'center', horizontal: 'left' }}
      >
        <Box sx={{ p: 2, width: 320 }}>
          <Autocomplete
            size="small"
            options={props.options}
            value={selected}
            onChange={(_e, opt) => {
              props.onSelect(opt?.id ?? null);
              setAnchorEl(null);
            }}
            getOptionLabel={(o) => o?.label ?? ''}
            isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                placeholder={props.searchPlaceholder}
                // Stop keydown from bubbling to the parent column Menu,
                // which would otherwise interpret single-character keys
                // as typeahead navigation and swallow them before they
                // reach the input.
                onKeyDown={(e) => e.stopPropagation()}
              />
            )}
          />
        </Box>
      </Popover>
    </>
  );
}

function ProjectFilterMenuItem() {
  const dispatch = useDispatch();
  const projectIdFilter = useSelector(
    (s: RootState) => s.deliverables.projectIdFilter,
  );
  const { data: resp } = useGetApiProjectsQuery({ pageSize: 100 });
  const options: FilterOption[] = (resp?.projects ?? [])
    .filter((p) => p?.projectId && p?.title)
    .map((p) => ({ id: p.projectId!, label: p.title! }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  return (
    <FilterMenuItem
      label="Filter by Project"
      clearLabel="Clear Project filter"
      options={options}
      selectedId={projectIdFilter}
      onSelect={(id) => dispatch(setProjectIdFilter(id))}
      searchPlaceholder="Search Projects…"
    />
  );
}

function TypeFilterMenuItem() {
  const dispatch = useDispatch();
  const typeIdFilter = useSelector(
    (s: RootState) => s.deliverables.typeIdFilter,
  );
  const { data: types } = useGetApiProgramsAvailableDeliverableTypesQuery();
  const options: FilterOption[] = (types ?? [])
    .filter((t) => t?.deliverableTypeId && t?.title)
    .map((t) => ({
      id: t.deliverableTypeId!,
      label: t.abbreviation ? `${t.abbreviation} — ${t.title}` : t.title!,
    }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  return (
    <FilterMenuItem
      label="Filter by Type"
      clearLabel="Clear Type filter"
      options={options}
      selectedId={typeIdFilter}
      onSelect={(id) => dispatch(setTypeIdFilter(id))}
      searchPlaceholder="Search Types…"
    />
  );
}

function AuthorFilterMenuItem() {
  const dispatch = useDispatch();
  const authorIdFilter = useSelector(
    (s: RootState) => s.deliverables.authorIdFilter,
  );
  // pageSize=100 is the contacts endpoint's max page. For tenants beyond
  // that, users can still scroll — Autocomplete is searchable — but we'd
  // ultimately want a typeahead-backed remote search.
  const { data: contacts } = useGetApiContactsQuery({ page: 1, pageSize: 100 });
  const options: FilterOption[] = (contacts?.items ?? [])
    .filter((c) => c?.contactId && c?.name)
    .map((c) => ({ id: c.contactId!, label: c.name! }))
    .sort((a, b) =>
      a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }),
    );
  return (
    <FilterMenuItem
      label="Filter by Author"
      clearLabel="Clear Author filter"
      options={options}
      selectedId={authorIdFilter}
      onSelect={(id) => dispatch(setAuthorIdFilter(id))}
      searchPlaceholder="Search Authors…"
    />
  );
}

// Per-field map of which column gets which filter slot. Anything not in
// the map falls through to MUI's default column menu.
const FILTER_BY_FIELD: Record<string, React.ComponentType> = {
  project: ProjectFilterMenuItem,
  deliverableType: TypeFilterMenuItem,
  author: AuthorFilterMenuItem,
};

export function DeliverablesColumnMenu(props: GridColumnMenuProps) {
  const Slot = FILTER_BY_FIELD[props.colDef.field];
  if (!Slot) {
    return <GridColumnMenu {...props} />;
  }
  return (
    <GridColumnMenu
      {...props}
      slots={{
        // displayOrder under 10 → appears above the standard items.
        columnMenuUserItem: Slot,
      }}
      slotProps={{
        columnMenuUserItem: {
          displayOrder: 5,
        },
      }}
    />
  );
}
